import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import DateField from "@/components/DateField";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import TimeOnlyField from "@/components/TimeOnlyField";
import { todayLocal, fmtDate } from "@/lib/timefmt";
import { Plus, Pencil, Trash2, X, Users, ChevronDown, ChevronRight, Lock, CalendarCheck } from "lucide-react";
import MembershipSetup from "@/components/MembershipSetup";
import TaskTimeline from "@/components/TaskTimeline";

const min2hm = (v) => `${String(Math.floor(v / 60)).padStart(2, "0")}:${String(v % 60).padStart(2, "0")}`;
const inputCls = "w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm";
const labelCls = "block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5";

export default function CommitmentsPage() {
  const { user } = useAuth();
  const [types, setTypes] = useState([]);
  const [unitMembers, setUnitMembers] = useState([]);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ date: todayLocal(), task_type_id: "", start: "", end: "", end_date: "", mode: "timed", name: "", remarks: "" });
  const [selMembers, setSelMembers] = useState([]);
  const [memberPick, setMemberPick] = useState("");
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [pageView, setPageView] = useState("list");
  const [listSearch, setListSearch] = useState("");
  const [listSort, setListSort] = useState("date_asc");
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulk, setBulk] = useState({ task_type_id: "", start: "", end: "", remarks: "" });
  const [bulkBusy, setBulkBusy] = useState(false);
  const privileged = (user?.privilege || "user") !== "user" || user?.role === "admin" || !!user?.admin_level;

  const load = () => {
    const to = new Date();
    to.setDate(to.getDate() + 14);
    const from = new Date();
    from.setDate(from.getDate() - 13);
    const iso = (d) => d.toISOString().slice(0, 10);
    api.get(`/commitments?from_date=${iso(from)}&to_date=${iso(to)}`).then((r) => setItems(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (!user?.company_id) return;
    api.get(`/task-types?company_id=${user.company_id}`).then((r) => setTypes(r.data)).catch(() => {});
    api.get("/org/unit-members").then((r) => setUnitMembers(r.data)).catch(() => {});
    load();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const myTypes = useMemo(
    () => types.filter((t) => t.kind === "commitment" && (!t.role_ids?.length || t.role_ids.includes(user?.role_id))),
    [types, user],
  );
  const nameOf = (c, id) => {
    const i = (c.member_ids || []).indexOf(id);
    return unitMembers.find((m) => m.id === id)?.name || (c.member_names || [])[i] || "Member";
  };
  const canEdit = (c) => c.created_by === user?.id || privileged;

  const resetForm = () => {
    setForm({ date: todayLocal(), task_type_id: "", start: "", end: "", end_date: "", mode: "timed", name: "", remarks: "" });
    setSelMembers([]);
    setMemberPick("");
    setEditId(null);
  };

  const addMember = () => {
    if (!memberPick || selMembers.includes(memberPick)) return;
    setSelMembers((s) => [...s, memberPick]);
    setMemberPick("");
  };

  const submit = async () => {
    if (!form.task_type_id) { toast.error("Pick a commitment type"); return; }
    if (form.mode === "timed" && (!form.start || !form.end)) { toast.error("Set start/stop times"); return; }
    if (form.mode === "multi_day" && !form.end_date) { toast.error("Pick an end date"); return; }
    setSaving(true);
    try {
      const payload = {
        task_type_id: form.task_type_id, date: form.date, mode: form.mode,
        start: form.mode === "timed" ? form.start : null, end: form.mode === "timed" ? form.end : null,
        end_date: form.mode === "multi_day" ? form.end_date : null,
        name: form.name.trim() || null, remarks: form.remarks.trim() || null, member_ids: selMembers,
      };
      if (editId) await api.put(`/commitments/${editId}`, payload);
      else await api.post("/commitments", payload);
      toast.success(editId ? "Commitment updated" : "Commitment added");
      resetForm();
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const startEdit = (c) => {
    setEditId(c.id);
    setForm({ date: c.date, task_type_id: c.task_type_id, start: c.start, end: c.end, end_date: c.end_date || "", mode: c.mode || "timed", name: c.name || "", remarks: c.remarks || "" });
    setSelMembers((c.member_ids || []).filter((id) => id !== user?.id));
    setMemberPick("");
    setFormOpen(true);
  };

  const del = async (c) => {
    if (!window.confirm(`Delete ${c.task_type_name} on ${fmtDate(c.date)}?`)) return;
    try { await api.delete(`/commitments/${c.id}`); toast.success("Commitment deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const bulkDelete = async () => {
    if (!selected.size) return;
    if (!window.confirm(`This will delete all ${selected.size} commitments. Proceed?`)) return;
    let ok = 0, fail = 0;
    for (const id of selected) {
      try { await api.delete(`/commitments/${id}`); ok += 1; } catch { fail += 1; }
    }
    if (ok) toast.success(`Deleted ${ok} commitment(s)`);
    if (fail) toast.error(`${fail} could not be deleted — only the creator or a supervisor can delete`);
    setSelected(new Set());
    load();
  };

  const bulkEdit = async () => {
    const changes = {};
    if (bulk.task_type_id) changes.task_type_id = bulk.task_type_id;
    if (bulk.start) changes.start = bulk.start;
    if (bulk.end) changes.end = bulk.end;
    if (bulk.remarks.trim()) changes.remarks = bulk.remarks.trim();
    if (!Object.keys(changes).length) { toast.error("Set at least one field to change"); return; }
    if (!window.confirm(`This will change all ${selected.size} commitments. Proceed?`)) return;
    setBulkBusy(true);
    let ok = 0, fail = 0;
    for (const c of items.filter((x) => selected.has(x.id))) {
      try {
        await api.put(`/commitments/${c.id}`, {
          task_type_id: changes.task_type_id || c.task_type_id,
          date: c.date,
          mode: (changes.start || changes.end) ? "timed" : (c.mode || "timed"),
          start: changes.start || c.start,
          end: changes.end || c.end,
          end_date: c.end_date || null,
          name: c.name || null,
          remarks: changes.remarks !== undefined ? changes.remarks : c.remarks,
          member_ids: null,
        });
        ok += 1;
      } catch { fail += 1; }
    }
    setBulkBusy(false);
    setBulkOpen(false);
    if (ok) toast.success(`Updated ${ok} commitment(s)`);
    if (fail) toast.error(`${fail} could not be updated — only the creator or a supervisor can edit`);
    setSelected(new Set());
    load();
  };

  if (!user?.company_id || !user?.role_id) {
    return <MembershipSetup title="Commitments" message="To log commitments, join a company and pick your crew role. One-time setup." />;
  }

  const filtered = items.filter((c) => {
    if (!listSearch) return true;
    const q = listSearch.toLowerCase();
    return `${c.task_type_name} ${c.name || ""} ${c.remarks || ""} ${(c.member_names || []).join(" ")}`.toLowerCase().includes(q);
  });
  const sorted = [...filtered].sort((a, b) => {
    if (listSort === "type") return a.task_type_name.localeCompare(b.task_type_name) || a.date.localeCompare(b.date) || a.start.localeCompare(b.start);
    if (listSort === "date_desc") return b.date.localeCompare(a.date) || a.start.localeCompare(b.start);
    return a.date.localeCompare(b.date) || a.start.localeCompare(b.start);
  });
  const grouped = sorted.reduce((acc, c) => { (acc[c.date] = acc[c.date] || []).push(c); return acc; }, {});
  const dates = Object.keys(grouped);

  return (
    <div data-testid="commitments-page">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Operations</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">Commitments</h1>
        <p className="text-slate-400 mt-1 text-sm">Appointments and obligations involving your crew — they show on everyone's timeline and duty day.</p>
      </div>

      <div className="flex gap-1 mb-5 border-b border-slate-800" data-testid="commitments-view-tabs">
        {[["list", "Commitments List"], ["timeline", "Timeline"]].map(([id, label]) => (
          <button key={id} onClick={() => setPageView(id)} data-testid={`commitments-view-${id}`}
            className={`px-4 py-2.5 text-sm font-medium -mb-px border-b-2 transition-colors ${pageView === id ? "border-blue-500 text-white" : "border-transparent text-slate-400 hover:text-slate-200"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60 mb-6" data-testid="commitment-form">
          <div className="flex items-center justify-between cursor-pointer" onClick={() => setFormOpen((o) => !o)} data-testid="commitment-form-header">
            <div className="font-heading text-lg font-semibold">{editId ? "Edit commitment" : "Add a commitment"}</div>
            <button onClick={() => setFormOpen((o) => !o)} data-testid="commitment-form-toggle"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-slate-800 hover:bg-slate-700 text-xs text-slate-300 transition-colors">
              {formOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              {formOpen ? "Hide" : "Show"}
            </button>
          </div>
          {formOpen && (<>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className={labelCls}>Date</label>
                <DateField value={form.date} onChange={(v) => setForm({ ...form, date: v })} testId="commitment-date" />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Commitment type</label>
                <select value={form.task_type_id} onChange={(e) => setForm({ ...form, task_type_id: e.target.value })} data-testid="commitment-type-select"
                  className={inputCls}>
                  <option value="">Select…</option>
                  {myTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                {myTypes.length === 0 && <p className="text-[11px] text-slate-500 mt-1.5">No commitment types configured for your role yet — ask an admin.</p>}
              </div>
              <TimeOnlyField label="Start" testId="commitment-start" value={form.start} onChange={(v) => setForm({ ...form, start: v })} />
              <TimeOnlyField label="Stop" testId="commitment-end" value={form.end} onChange={(v) => setForm({ ...form, end: v })} />
              <div>
                <label className={labelCls}>Mode</label>
                <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value, end_date: "" })} data-testid="commitment-mode"
                  className={inputCls}>
                  <option value="timed">Timed (set start/stop)</option>
                  <option value="full_day">Full day (duty start → duty end)</option>
                  <option value="multi_day">Multi-day (duty start first day → duty end last day)</option>
                </select>
                {form.mode !== "timed" && (
                  <p className="text-[11px] text-slate-500 mt-1.5">Times default to your duty {form.mode === "full_day" ? "start and end that day" : "start on day one and duty end on the last day"}.</p>
                )}
              </div>
              {form.mode === "multi_day" && (
                <div>
                  <label className={labelCls}>End date</label>
                  <DateField value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} testId="commitment-end-date" />
                </div>
              )}
              <div>
                <label className={labelCls}>Name (optional — defaults to the type)</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="commitment-name"
                  className={inputCls} placeholder="e.g. Medical appointment" />
              </div>
            </div>
            <div className="mt-3">
              <label className={labelCls}>Remarks (optional)</label>
              <input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} data-testid="commitment-remarks"
                className={inputCls} placeholder="Notes for this commitment" />
            </div>

            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-2">Who's involved (you're added automatically)</div>
              <div className="flex flex-wrap items-center gap-2">
                <select value={memberPick} onChange={(e) => setMemberPick(e.target.value)} data-testid="commitment-member-select"
                  className="px-3 py-2 rounded-md bg-slate-950 border border-slate-800 text-sm">
                  <option value="">Select member…</option>
                  {unitMembers.filter((m) => m.id !== user?.id && !selMembers.includes(m.id)).map((m) => (
                    <option key={m.id} value={m.id}>{m.name}{m.role_name ? ` · ${m.role_name}` : ""}</option>
                  ))}
                </select>
                <button onClick={addMember} disabled={!memberPick} data-testid="commitment-add-member"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-xs disabled:opacity-40">
                  <Plus className="h-3.5 w-3.5" /> Add
                </button>
              </div>
              {selMembers.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2" data-testid="commitment-members">
                  {selMembers.map((id) => (
                    <span key={id} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-800 border border-slate-700 text-xs">
                      <Users className="h-3 w-3 text-slate-500" /> {unitMembers.find((m) => m.id === id)?.name || "Member"}
                      <button onClick={() => setSelMembers((s) => s.filter((x) => x !== id))}
                        data-testid={`commitment-remove-member-${id}`} className="text-slate-500 hover:text-rose-300"><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-2 mt-5">
              <button onClick={submit} disabled={saving} data-testid="commitment-submit-btn"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-colors disabled:opacity-50">
                <CalendarCheck className="h-4 w-4" /> {saving ? "Saving…" : "Save commitment"}
              </button>
              {editId && (
                <button onClick={resetForm} data-testid="commitment-cancel-edit"
                  className="px-4 py-2.5 rounded-md bg-slate-800 hover:bg-slate-700 text-sm">Cancel</button>
              )}
            </div>
          </>)}
        </div>

      {pageView === "timeline" && (
        <TaskTimeline userId={user?.id} companyId={user?.company_id} locationId={user?.location_id} refreshKey={items} onChanged={load}
          kindFilter="commitment" endpoint="/commitments" tasksLabel="Commitments"
          onEditTask={(t) => { if (!canEdit(t)) { toast.error("Only the creator or a supervisor can edit this commitment"); return; } startEdit(t); document.querySelector('[data-testid="commitment-form"]')?.scrollIntoView({ behavior: "smooth" }); }}
          onQuickAdd={(d, hm) => {
            const [h, m] = hm.split(":").map(Number);
            setEditId(null);
            setSelMembers([]);
            setForm((f) => ({ ...f, date: d, start: hm, end: min2hm(Math.min(1439, h * 60 + m + 60)) }));
            setFormOpen(true);
            document.querySelector('[data-testid="commitment-form"]')?.scrollIntoView({ behavior: "smooth" });
          }} />
      )}

      {pageView === "list" && (<>
        <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="commitment-list-toolbar">
          <input value={listSearch} onChange={(e) => setListSearch(e.target.value)} placeholder="Search type, name, remarks, members…" data-testid="commitment-list-search"
            className="px-3 py-2.5 rounded-md bg-slate-900 border border-slate-800 text-sm flex-1 min-w-[180px]" />
          <select value={listSort} onChange={(e) => setListSort(e.target.value)} data-testid="commitment-list-sort"
            className="px-3 py-2.5 rounded-md bg-slate-900 border border-slate-800 text-sm">
            <option value="date_asc">Oldest first</option>
            <option value="date_desc">Newest first</option>
            <option value="type">By type</option>
          </select>
          <label className="flex items-center gap-1.5 text-xs text-slate-400 px-1 cursor-pointer" title="Select all listed commitments">
            <input type="checkbox" className="accent-blue-600 h-3.5 w-3.5" data-testid="commitment-select-all"
              checked={sorted.length > 0 && sorted.every((c) => selected.has(c.id))}
              onChange={(e) => setSelected(e.target.checked ? new Set(sorted.map((c) => c.id)) : new Set())} />
            All
          </label>
          {selected.size > 0 && (
            <>
              <button onClick={() => { setBulk({ task_type_id: "", start: "", end: "", remarks: "" }); setBulkOpen(true); }} data-testid="commitment-bulk-edit-open"
                className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm transition-colors">
                <Pencil className="h-3.5 w-3.5" /> Edit selected ({selected.size})
              </button>
              <button onClick={bulkDelete} data-testid="commitment-bulk-delete"
                className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-md bg-rose-600/80 hover:bg-rose-500 text-white text-sm transition-colors">
                <Trash2 className="h-3.5 w-3.5" /> Delete selected ({selected.size})
              </button>
            </>
          )}
        </div>

        <div className="space-y-4" data-testid="commitment-list">
          {dates.length === 0 && (
            <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60 text-sm text-slate-500">No commitments match.</div>
          )}
          {dates.map((d) => (
            <div key={d}>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-2">{fmtDate(d)}</div>
              <div className="space-y-2">
                {grouped[d].map((c) => (
                  <div key={c.id} className="p-4 rounded-lg border border-slate-800 bg-slate-900/60"
                    style={c.color ? { borderLeft: `3px solid ${c.color}` } : undefined} data-testid={`commitment-card-${c.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <input type="checkbox" className="accent-blue-600 h-4 w-4 shrink-0" checked={selected.has(c.id)}
                            data-testid={`commitment-select-${c.id}`} onChange={() => toggleSelect(c.id)} />
                          <span className="font-medium text-sm">{c.name || c.task_type_name}</span>
                          {c.name && <span className="text-[10px] text-slate-500">{c.task_type_name}</span>}
                          <span className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-widest border"
                            style={c.color ? { color: c.color, borderColor: c.color, background: `${c.color}1f` } : undefined}
                            data-testid={`commitment-type-${c.id}`}>Commitment</span>
                          {c.fixed && <span title="Fixed — not moveable" className="inline-flex items-center"><Lock className="h-3 w-3 text-slate-500" /></span>}
                          <span className="text-xs font-mono-num text-slate-400">{c.start}–{c.end}</span>
                        </div>
                        {c.remarks && <div className="text-[11px] text-slate-400 mt-1.5 italic" data-testid={`commitment-remarks-${c.id}`}>{c.remarks}</div>}
                        {(c.member_ids || []).length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`commitment-members-${c.id}`}>
                            {c.member_ids.map((id) => (
                              <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700 text-[10px]">
                                <Users className="h-2.5 w-2.5 text-slate-500" /> {nameOf(c, id)}{id === c.created_by ? " (lead)" : ""}
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="text-[11px] text-slate-500 mt-1.5">
                          {c.created_by === user?.id ? "Added by you" : `Added by ${c.created_by_name}`}
                        </div>
                      </div>
                      {canEdit(c) && (
                        <div className="flex items-center gap-2 shrink-0">
                          <button onClick={() => { startEdit(c); setPageView("timeline"); window.scrollTo({ top: 0, behavior: "smooth" }); }} data-testid={`commitment-edit-${c.id}`}
                            className="p-2 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors"><Pencil className="h-3.5 w-3.5" /></button>
                          <button onClick={() => del(c)} data-testid={`commitment-delete-${c.id}`}
                            className="p-2 rounded-md bg-slate-800 hover:bg-rose-900/40 text-slate-300 hover:text-rose-300 transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </>)}
      <Dialog open={bulkOpen} onOpenChange={(o) => !o && setBulkOpen(false)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-md" data-testid="commitment-bulk-dialog">
          <DialogHeader><DialogTitle className="font-heading">Edit {selected.size} selected commitment(s)</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 -mt-1 mb-2">Fields left blank stay unchanged. Setting start/stop converts full-day/multi-day commitments to timed.</p>
          <div className="space-y-3">
            <div>
              <label className={labelCls}>Commitment type</label>
              <select value={bulk.task_type_id} onChange={(e) => setBulk({ ...bulk, task_type_id: e.target.value })} data-testid="commitment-bulk-type" className={inputCls}>
                <option value="">— keep current —</option>
                {myTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <TimeOnlyField label="Start" testId="commitment-bulk-start" value={bulk.start} onChange={(v) => setBulk({ ...bulk, start: v })} />
              <TimeOnlyField label="Stop" testId="commitment-bulk-end" value={bulk.end} onChange={(v) => setBulk({ ...bulk, end: v })} />
            </div>
            <div>
              <label className={labelCls}>Remarks</label>
              <input value={bulk.remarks} onChange={(e) => setBulk({ ...bulk, remarks: e.target.value })} data-testid="commitment-bulk-remarks" className={inputCls} placeholder="— keep current —" />
            </div>
            <button onClick={bulkEdit} disabled={bulkBusy} data-testid="commitment-bulk-apply"
              className="w-full py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium disabled:opacity-50 transition-colors">
              {bulkBusy ? "Applying…" : `Apply to ${selected.size} commitment(s)`}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
